import React from 'react';
import AppMenuBar from '~/app/containers/Genesis/Menu/AppMenuBar';
import AppFooter from '~/app/containers/Genesis/Footer/AppFooter';
import menuProps from 'lib/menuProps';
import footerProps from 'lib/footerProps';
import { useParams, useLocation } from 'react-router-dom';
import { createUseStyles } from 'react-jss';
import cx from 'classnames';
import ConfigContextProvider, { useConfig } from '~/app/contexts/ConfigContextProvider';
import { styled } from '@ff/ui';
import Box from '@ff/ui/Box';
import PATHS from '~/app/utils/paths';
import { getLocaleAwareMenuProps } from '~/app/utils/getLocaleAwareMenuProps';

const Layout = styled(Box)();
Layout.defaultProps = { as: 'section' };

const Content = styled(Box)({
  minHeight: '500px',
});
Content.defaultProps = { as: 'main' };

export default function HeaderFooterLayoutContainer(props) {
  return (
    <ConfigContextProvider>
      <HeaderFooterLayout {...props} />
    </ConfigContextProvider>
  );
}

const BASE_MENU_PROPS = window?.MENU_PROPS || menuProps;
const FOOTER_PROPS = window?.FOOTER_PROPS || footerProps;

function HeaderFooterLayout({ children }) {

  const c = useStyles();
  const params = useParams();
  const { locale } = params;
  const location = useLocation();

  const { showMenuBar, showFooter } = useConfig();
  
  // Get locale-aware menu props (applies Arabic translations for ae_ar locale)
  const MENU_PROPS = getLocaleAwareMenuProps(BASE_MENU_PROPS, locale);

  // /us/reserve, /MyFF/reservations, /us/preorder, /MyFF/preorders
  const isReserveUS = (params[0].includes('preorder') || params[0].includes('reserv')) && locale === 'us';
  const isEventUS = params[0].includes('event') && locale === 'us';
  const isPaymentUS = params[0].includes('payment') && locale === 'us';
  const isReferralUS = params[0].includes('referral') && locale === 'us';
  const isDesignUS = params[0].includes('design') && locale === 'us';
  const isLoginUS = params[0].includes('login') && locale === 'us';
  // Starts with account
  const isAccountUS = params[0].indexOf('account') === 0 && locale === 'us';
  
  // Check if the current page is the FX preorder page (for menu customization)
  const isFXPreorderPage = 
    location.pathname === `/${locale}${PATHS.RESERVE}${PATHS.FX_SIMPLE_PREORDER}` ||
    location.pathname === `/${locale}${PATHS.RESERVE}${PATHS.FX_SIMPLE_PREORDER_SUPER_ONE}` ||
    location.pathname === `/${locale}${PATHS.APP_FX_SIMPLE_PREORDER}` ||
    location.pathname === `/${locale}${PATHS.RESERVE}${PATHS.FX_EARLY_ACCESS_PREORDER}` ||
    location.pathname === `/${locale}${PATHS.APP_FX_EARLY_ACCESS_PREORDER}` ||
    location.pathname === `/${locale}/advanceorder/fx` ||
    location.pathname === `/${locale}/h5/advanceorder/fx`;

  // Check if the current page is any FX Fleet page (for menu customization)
  const isFXFleetPage = location.pathname.startsWith(`/${locale}${PATHS.FX_FLEET}`);
  
  // Specific FX Fleet preorder page check (for footer hiding)
  const isFXFleetPreorderPage = location.pathname === `/${locale}${PATHS.FX_FLEET_PREORDER}`;

  // Check if the current page is the FF Robot preorder page (for footer hiding)
  const isFFRobotPreorderPage = 
    location.pathname === `/${locale}${PATHS.RESERVE}${PATHS.FF_ROBOT_PREORDER}` ||
    location.pathname === `/${locale}${PATHS.APP_FF_ROBOT_PREORDER}`;

  // Check if the current page is any FF Robot Business page (for menu customization)
  const isFFRobotBusinessPage = location.pathname.startsWith(`/${locale}${PATHS.FF_ROBOT_BUSINESS}`);

  // Specific FF Robot Business preorder page check (for footer hiding)
  const isFFRobotBusinessPreorderPage = location.pathname === `/${locale}${PATHS.FF_ROBOT_BUSINESS_PREORDER}`;

  // Check if footer should be hidden
  const shouldHideFooter = isFXPreorderPage || isFXFleetPreorderPage || isFFRobotPreorderPage || isFFRobotBusinessPreorderPage || isFFRobotBusinessPage;
  
  // Check if we're on AE locale (ae_ar or ae_en)
  const isAELocale = locale === 'ae_ar' || locale === 'ae_en';
  
  // Check if we're on Arabic locale for RTL support
  const isRTL = locale === 'ae_ar';
  
  // For AE locales, construct absolute URL to prevent ff-ui from overriding with https://www.ff.com
  const getLogoLink = () => {
    if (isAELocale) {
      // Use absolute URL with current origin to ensure logo link works correctly
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      return `${origin}/${locale}/`;
    }
    return undefined; // Use default for non-AE locales
  };
  
  // Customize MenuBar props for FX preorder and FX Fleet pages
  const customMenuProps = {
    ...MENU_PROPS,
    // For AE locales, always set the logo link to the AE home page
    ...(isAELocale && {
      logo_link: getLogoLink(), // Use snake_case to match menuProps
      logoLink: getLogoLink(),  // Use camelCase for Header2 component
    }),
    // Set custom styling for FX preorder and FX Fleet pages
    ...((isFXPreorderPage || isFXFleetPage) && { 
      logoType: 'FX SIMPLE', // FX logo for FX preorder and FX Fleet pages
      logo_link: `/${locale}${PATHS.FX_HOME}/`, // Use snake_case to match menuProps
      logoLink: `/${locale}${PATHS.FX_HOME}/`,  // Use camelCase for Header2 component - Enable logo link to FX home page
      float: true, // Make navigation sticky
      backgroundColor: 'white', // Ensure solid background when floating
      zIndex: 1000 // Ensure navigation stays above the form sidebar
    })
  };

  const contentClassName = isReserveUS || isEventUS || isReferralUS || isDesignUS || isPaymentUS ? c.reserveBackground : '';
  const newContentClassName = isAccountUS ? c.shadedBackground : '';
  const contentClsResolver = isReserveUS || isLoginUS ? c.clsResolver : '';
  const newLoginClsResolver = isLoginUS ? c.flex : '';

  // Show US footer region selector only if the page isn't available for other regions.
  const availableFooterRegions = isEventUS || isReferralUS ? ['US'] : undefined;

  return (
    <Layout className={cx('ant-layout', c.newLayoutDefault, newContentClassName)}>
      <div id="banner-portal" />
      {showMenuBar && <AppMenuBar {...customMenuProps} />}
      <Content
        className={cx('ant-layout-content', contentClsResolver, c.flex, c.contentPadding)}
      >
        {children}
      </Content>
      {showFooter && !shouldHideFooter && <AppFooter availableRegions={availableFooterRegions} dir={isRTL ? 'rtl' : 'ltr'} {...FOOTER_PROPS} />}
    </Layout>
  );
}

const useStyles = createUseStyles({
  newLayoutDefault: {
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  reserveBackground: {
    backgroundColor: '#ffffff',
  },
  shadedBackground: {
    backgroundColor: 'white',
  },
  clsResolver: {
    minHeight: 'calc(100vh - 88px)'
  },
  flex: {
    '@media(min-width: 600px)': {
      marginLeft: 'auto',
      marginRight: 'auto',
      maxWidth: '1920px',
      width: '100%',
      paddingTop: '24px',
      paddingRight: '24px',
      paddingLeft: '24px',
    },
  },
  contentPadding: {
    // Handled by MenuBar spacer
    paddingTop: '0px',
    paddingBottom: 0
  }
});
